import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from '@/i18n';
import { TenantIndicator } from '@/components/layout/TenantIndicator';
import { Menu, Moon, Sun } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

/**
 * Application header component
 *
 * Features:
 * - Mobile menu button
 * - Tenant indicator
 * - Theme toggle (dark/light)
 * - Language selector
 *
 * The user menu (logout, account settings, switch app) now lives in the
 * sidebar via UserSwitcherBlock (shared package).
 */
interface HeaderProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function Header({ onMenuClick, showMenuButton }: HeaderProps) {
  const { t } = useI18n();
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-background/95 backdrop-blur px-6">
      <div className="flex items-center gap-4">
        {showMenuButton && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="lg:hidden"
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">{t('common.menu')}</span>
          </Button>
        )}
        {/* Tenant Indicator */}
        <TenantIndicator />
      </div>

      <div className="flex items-center gap-2">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          title={t('common.toggleTheme')}
        >
          {theme === 'dark' ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
          <span className="sr-only">{t('common.toggleTheme')}</span>
        </Button>

        {/* Language Selector */}
        <LanguageSelector />
      </div>
    </header>
  );
}
